import { useState, useEffect, useCallback } from 'react';
import { fetchBuyerReservations, BuyerReservation } from '../utils/properties';
import useTokenStore from '../store/store';

// The signed-in buyer's reservation history for the /dashboard "My
// Reservations" card. The fetch is effect-scoped with a cancellation guard so
// a response that lands after logout or a token change is discarded — a stale
// request must never paint another session's reservations. fetchBuyerReservations
// fails closed to [], so the card degrades to its empty state on any error.
export const useBuyerReservations = () => {
  const [reservations, setReservations] = useState<BuyerReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const getToken = useTokenStore((state) => state.token);

  useEffect(() => {
    // Reset on every run: rows are buyer-specific, so a direct A → B token
    // swap must never leave A's history rendered while B's request is in
    // flight. Loading resets too — a cancelled in-flight request's finally is
    // ignored, so logout must not strand the spinner.
    setReservations([]);
    setLoading(false);
    if (!getToken) {
      return;
    }
    let active = true;
    setLoading(true);
    fetchBuyerReservations({ token: getToken })
      .then((data) => {
        if (active) setReservations(data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [getToken, reloadKey]);

  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    reservations,
    loading,
    refetch,
  };
};
